/*
  Schichtplan für Mitarbeitende: nur lesen.

  Zeitraum, Monat/Woche und Zeilen nach Mitarbeitenden oder Abteilungen - mehr
  nicht. Das Raster ist dasselbe wie in der Planung; es bekommt nur keine
  Bearbeitungs-Handler (kein Auswählen, Verschieben, Kopieren, keine
  Tagesnotizen). Doppelklick öffnet die Details.

  Daten kommen von /api/web/employee/shiftplan: nur Freigegebenes, die eigene
  Person zuerst, danach nach Vorname.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box } from "@mui/material";
import AppLoadingOverlay from "../../../design-system/components/AppLoadingOverlay";
import { normalizeApiError } from "../../shiftplan_v2/api/errors";
import { mapSchedulerPayload, mapWorkloadPayload } from "../../shiftplan_v2/api/mappers";
import { setActivePeriod } from "../../shiftplan_v2/api/period";
import { getEmployeeShiftplan, getEmployeeShiftplanWorkload } from "../../shiftplan_v2/api/shiftplanApi";
import {
  addMonthsToDate,
  addWeeksToDate,
  formatMonthTitle,
  formatWeekTitle,
  getMonthDays,
  getWeekDays,
} from "../../shiftplan_v2/domain/date/month";
import {
  buildVisibleDepartments,
  buildVisibleEmployees,
  filterShiftsByDays,
  getDepartmentRowId,
  getShiftDepartmentRowId,
  groupPublicHolidayNamesByDate,
  groupShiftsByCell,
} from "../../shiftplan_v2/domain/model/selectors";
import { groupUserHolidaysByCell } from "../../shiftplan_v2/domain/model/userHolidays";
import { buildEmployeeWorkloadMap } from "../../shiftplan_v2/domain/model/workload";
import ShiftplanMonthGrid from "../../shiftplan_v2/features/grid/ShiftplanMonthGrid";
import { ShiftplanPeriodControls } from "../../shiftplan_v2/features/toolbar/ShiftplanV2Toolbar";
import EntryDetailsDialog from "./EntryDetailsDialog";
import { buildEntryDetails } from "./entryDetails";

const EMPLOYEE_SECTIONS = { ownLabel: "Dein Plan", teamLabel: "Team" };

const EMPTY_DATA = {
  employees: [],
  shifts: [],
  absenceOptions: [],
  userHolidays: [],
  departments: [],
  publicHolidays: [],
  meta: {},
};

export default function EmployeeShiftplanPage() {
  const [referenceDate, setReferenceDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState("month");
  const [rowMode, setRowMode] = useState("employee");
  const [data, setData] = useState(EMPTY_DATA);
  const [currentEmployeeId, setCurrentEmployeeId] = useState("");
  const [backendWorkload, setBackendWorkload] = useState({ periodKey: "", rows: {} });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  const days = useMemo(
    () => (viewMode === "week" ? getWeekDays(referenceDate) : getMonthDays(referenceDate)),
    [referenceDate, viewMode]
  );
  const periodLabel = useMemo(
    () => (viewMode === "week" ? formatWeekTitle(referenceDate) : formatMonthTitle(referenceDate)),
    [referenceDate, viewMode]
  );
  const periodStartDate = days[0]?.key || "";
  const periodEndDate = days[days.length - 1]?.key || "";
  const workloadPeriodKey = `${periodStartDate}::${periodEndDate}`;

  // Beim schnellen Blättern dürfen sich Antworten nicht überholen: nur die
  // zuletzt gestellte Anfrage schreibt den Zustand.
  const requestSequenceRef = useRef(0);
  const loadShiftplan = useCallback(async () => {
    const requestId = (requestSequenceRef.current += 1);
    setIsLoading(true);
    setError("");
    try {
      const payload = await getEmployeeShiftplan();
      if (requestId === requestSequenceRef.current) {
        setData(mapSchedulerPayload(payload));
        setCurrentEmployeeId(String(payload?.current_user_id ?? ""));
      }
    } catch (loadError) {
      if (requestId === requestSequenceRef.current) setError(normalizeApiError(loadError).message);
    } finally {
      if (requestId === requestSequenceRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!periodStartDate || !periodEndDate) return;
    setActivePeriod({ startDate: periodStartDate, endDate: periodEndDate });
    loadShiftplan();
  }, [loadShiftplan, periodEndDate, periodStartDate]);

  const periodShifts = useMemo(() => filterShiftsByDays(data.shifts, days), [data.shifts, days]);
  const departmentOrder = useMemo(
    () => data.departments.map((department) => getDepartmentRowId(department?.name ?? department?.label ?? department)),
    [data.departments]
  );
  // Die Reihenfolge der Mitarbeitenden kommt fertig vom Server (eigene Person
  // zuerst, dann Vorname) und bleibt hier unverändert.
  const rows = useMemo(
    () =>
      rowMode === "department"
        ? buildVisibleDepartments({
            departments: data.departments,
            shifts: periodShifts,
            departmentOrder,
            includeUnassigned: true,
          })
        : buildVisibleEmployees({ employees: data.employees, shifts: periodShifts }),
    [data.departments, data.employees, departmentOrder, periodShifts, rowMode]
  );
  const visibleShifts = useMemo(() => {
    const rowIds = new Set(rows.map((row) => row.id));
    return periodShifts.filter((shift) =>
      rowIds.has(rowMode === "department" ? getShiftDepartmentRowId(shift) : shift.employeeId)
    );
  }, [periodShifts, rowMode, rows]);
  const shiftsByCell = useMemo(() => groupShiftsByCell(visibleShifts, rowMode), [rowMode, visibleShifts]);
  const userHolidaysByCell = useMemo(
    () => groupUserHolidaysByCell({ holidays: data.userHolidays, days }),
    [data.userHolidays, days]
  );
  const publicHolidayNamesByDate = useMemo(
    () => groupPublicHolidayNamesByDate(data.publicHolidays),
    [data.publicHolidays]
  );
  /*
    Stunden wie in der Planung (ShiftplanV2Page): die Rechnung des Servers
    gilt, die lokale überbrückt nur, bis sie da ist. Der Server zählt hier nur
    Freigegebenes.
  */
  useEffect(() => {
    if (!periodStartDate || !periodEndDate || !data.employees.length) return undefined;

    let isCurrent = true;
    getEmployeeShiftplanWorkload({ startDate: periodStartDate, endDate: periodEndDate })
      .then((payload) => {
        if (isCurrent) setBackendWorkload({ periodKey: workloadPeriodKey, rows: mapWorkloadPayload(payload) });
      })
      .catch(() => {
        if (isCurrent) setBackendWorkload({ periodKey: "", rows: {} });
      });
    return () => {
      isCurrent = false;
    };
  }, [data.employees.length, periodEndDate, periodStartDate, workloadPeriodKey]);
  const vacationAbsenceIds = useMemo(
    () => new Set(data.absenceOptions.filter((option) => option.isVacation).map((option) => String(option.id))),
    [data.absenceOptions]
  );
  const localWorkloadByEmployeeId = useMemo(
    () =>
      rowMode === "department"
        ? {}
        : buildEmployeeWorkloadMap({
            employees: rows,
            shifts: visibleShifts,
            days,
            workingDaysPerWeek: data.meta?.workingDays || 5,
            absenceOptions: data.absenceOptions,
            vacationAbsenceIds,
            userHolidays: data.userHolidays,
          }),
    [data.absenceOptions, data.meta?.workingDays, data.userHolidays, days, rowMode, rows, vacationAbsenceIds, visibleShifts]
  );
  const workloadByEmployeeId = useMemo(() => {
    if (rowMode === "department") return {};
    if (backendWorkload.periodKey !== workloadPeriodKey) return localWorkloadByEmployeeId;
    return { ...localWorkloadByEmployeeId, ...backendWorkload.rows };
  }, [backendWorkload, localWorkloadByEmployeeId, rowMode, workloadPeriodKey]);
  const employeesById = useMemo(
    () => Object.fromEntries(data.employees.map((employee) => [employee.id, employee])),
    [data.employees]
  );
  const details = useMemo(() => buildEntryDetails(detail, employeesById), [detail, employeesById]);

  const movePeriod = (step) =>
    setReferenceDate((current) =>
      viewMode === "week" ? addWeeksToDate(current, step) : addMonthsToDate(current, step)
    );

  return (
    <Box className="spv2-root spv2-page-shell">
      <div className="spv2-toolbar-wrap">
        <div className="spv2-toolbar-row">
          <ShiftplanPeriodControls
            viewMode={viewMode}
            rowMode={rowMode}
            referenceDate={referenceDate}
            periodLabel={periodLabel}
            onViewModeChange={setViewMode}
            onRowModeChange={setRowMode}
            onPreviousPeriod={() => movePeriod(-1)}
            onNextPeriod={() => movePeriod(1)}
            onPeriodSelect={setReferenceDate}
          />
        </div>
      </div>

      {error ? (
        <Alert severity="error" className="spv2-alert">
          {error}
        </Alert>
      ) : null}

      {isLoading && !data.shifts.length && !rows.length ? (
        <Box className="spv2-content-loading">
          <AppLoadingOverlay open scope="contained" blocking={false} label="Laden…" />
        </Box>
      ) : (
        <Box className="spv2-grid-with-side-panel">
          <ShiftplanMonthGrid
            viewMode={viewMode}
            rowMode={rowMode}
            days={days}
            rows={rows}
            shiftsByCell={shiftsByCell}
            userHolidaysByCell={userHolidaysByCell}
            publicHolidayNamesByDate={publicHolidayNamesByDate}
            shiftDisplayMode={data.meta?.shiftDisplayMode}
            workloadByEmployeeId={workloadByEmployeeId}
            currentEmployeeId={currentEmployeeId}
            employeeSections={EMPLOYEE_SECTIONS}
            onShiftDoubleClick={({ shift }) => setDetail({ type: "shift", entry: shift })}
            onUserHolidayDoubleClick={({ holiday }) => setDetail({ type: "holiday", entry: holiday })}
          />
        </Box>
      )}

      <EntryDetailsDialog details={details} onClose={() => setDetail(null)} />
    </Box>
  );
}
